package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.util.encodeBase64
import br.com.vvrefeicoes.portal.core.util.file.PickedFile
import br.com.vvrefeicoes.portal.core.util.file.pickPdfFile
import br.com.vvrefeicoes.portal.core.util.savePdf
import br.com.vvrefeicoes.portal.domain.model.RmpFieldKind
import br.com.vvrefeicoes.portal.domain.model.RmpFormField
import br.com.vvrefeicoes.portal.domain.model.RmpFormSchema
import br.com.vvrefeicoes.portal.domain.model.RmpFormType
import br.com.vvrefeicoes.portal.domain.model.RmpRequestAttachment
import br.com.vvrefeicoes.portal.domain.model.RmpRequestDetail
import br.com.vvrefeicoes.portal.domain.repository.RmpRequestRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

/** Mensagem do rodapé do formulário (sucesso ou erro). */
data class RmpNewFeedback(
    val message: String,
    val isError: Boolean,
)

internal const val RMP_STATUS_DRAFT = "rascunho"
internal const val RMP_STATUS_PENDING = "pendente"

/** Campos que a API mantém num RMP enviado. */
private val RMP_FIELDS_LOCKED_AFTER_SUBMIT =
    setOf(RMP_UNIT_FIELD, RMP_COST_CENTER_FIELD, "empresa_origem", "empresa_destino")

/**
 * "Novo RMP": o formulário de cada tipo é montado a partir do esquema da API. Enviar grava o formulário como
 * rascunho, sobe os PDFs escolhidos e só então muda para "pendente" (a API confere os obrigatórios, inclusive anexos);
 * se a API recusar, o mesmo rascunho é reaproveitado no próximo envio.
 */
class RmpNewController(
    private val repository: RmpRequestRepository,
    private val scope: CoroutineScope,
) {
    var loading by mutableStateOf(true)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var schema by mutableStateOf<RmpFormSchema?>(null)
        private set

    var selectedType by mutableStateOf<RmpFormType?>(null)
        private set

    var values by mutableStateOf<Map<String, RmpFieldValue>>(emptyMap())
        private set

    /** Formulário em edição (rascunho já salvo); null = formulário novo. */
    var current by mutableStateOf<RmpRequestDetail?>(null)
        private set

    /** PDFs escolhidos e ainda não enviados, por campo. */
    var pendingFiles by mutableStateOf<Map<String, List<PickedFile>>>(emptyMap())
        private set

    var saving by mutableStateOf(false)
        private set

    var feedback by mutableStateOf<RmpNewFeedback?>(null)
        private set

    private var initialized = false

    /** RMP já enviado em edição (`/rmp/approvals`); null = formulário novo do solicitante. */
    var editId: String? = null
        private set

    val isEditingSubmitted: Boolean
        get() = editId != null

    val submittableTypes: List<RmpFormType>
        get() = schema?.types.orEmpty()

    /** Num RMP enviado, a unidade, o centro de custo e a origem/destino não mudam (definem quem aprova). */
    fun isLocked(field: RmpFormField): Boolean = isEditingSubmitted && field.name in RMP_FIELDS_LOCKED_AFTER_SUBMIT

    val attachments: List<RmpRequestAttachment>
        get() = current?.attachments.orEmpty()

    val visibleFields: List<RmpFormField>
        get() = selectedType?.fields.orEmpty().filter { isVisible(it, values) && it.kind != RmpFieldKind.UNKNOWN }

    /** Carrega o esquema do formulário novo ou, com [editId], o RMP enviado que o usuário pode editar. */
    fun init(editId: String? = null) {
        if (initialized) return
        initialized = true
        this.editId = editId
        scope.launch {
            loading = true
            errorMessage = null
            try {
                if (editId == null) {
                    schema = repository.loadSchema()
                } else {
                    val loaded = repository.loadEditSchema(editId)
                    val request = repository.loadApproval(editId).request
                    val type = loaded.types.first()
                    schema = loaded
                    current = request
                    selectedType = type
                    values = jsonToValues(type.fields, request.data)
                }
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível carregar o formulário."
            } finally {
                loading = false
            }
        }
    }

    fun retry() {
        initialized = false
        init(editId)
    }

    /** Troca o tipo de um formulário novo; num rascunho salvo o tipo não muda. */
    fun selectType(value: String) {
        if (current != null || value == selectedType?.value) return
        val type = schema?.types?.firstOrNull { it.value == value } ?: return
        selectedType = type
        values = defaultValues(type.fields)
        pendingFiles = emptyMap()
        feedback = null
    }

    fun setValue(
        name: String,
        value: RmpFieldValue,
    ) {
        values = values + (name to value)
    }

    fun toggleChoice(
        name: String,
        option: String,
    ) {
        val selected = (values[name] as? RmpFieldValue.Choices)?.values.orEmpty()
        setValue(name, RmpFieldValue.Choices(if (option in selected) selected - option else selected + option))
    }

    fun pickFile(field: RmpFormField) {
        val name = field.name ?: return
        scope.launch {
            val file = pickPdfFile() ?: return@launch
            if (!file.name.endsWith(".pdf", ignoreCase = true)) {
                feedback = RmpNewFeedback("Escolha um arquivo PDF.", isError = true)
                return@launch
            }
            val chosen = pendingFiles[name].orEmpty()
            pendingFiles = pendingFiles + (name to if (field.kind == RmpFieldKind.FILE) listOf(file) else chosen + file)
        }
    }

    fun removePendingFile(
        field: String,
        index: Int,
    ) {
        val files = pendingFiles[field].orEmpty().filterIndexed { position, _ -> position != index }
        pendingFiles = if (files.isEmpty()) pendingFiles - field else pendingFiles + (field to files)
    }

    fun removeAttachment(attachment: RmpRequestAttachment) {
        val request = current ?: return
        runSaving {
            if (isEditingSubmitted) {
                repository.deleteSubmittedAttachment(request.id, attachment.id)
                current = repository.loadApproval(request.id).request
            } else {
                repository.deleteAttachment(request.id, attachment.id)
                current = repository.load(request.id)
            }
        }
    }

    fun downloadAttachment(attachment: RmpRequestAttachment) {
        val request = current ?: return
        scope.launch {
            try {
                val bytes =
                    if (isEditingSubmitted) {
                        repository.downloadApprovalAttachment(request.id, attachment.id)
                    } else {
                        repository.downloadAttachment(request.id, attachment.id)
                    }
                savePdf(attachment.fileName, encodeBase64(bytes))
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                feedback = RmpNewFeedback(throwable.message ?: "Não foi possível baixar o arquivo.", isError = true)
            }
        }
    }

    /** Começa um formulário em branco. */
    fun newForm() {
        current = null
        selectedType = null
        values = emptyMap()
        pendingFiles = emptyMap()
        feedback = null
    }

    fun dismissFeedback() {
        feedback = null
    }

    val isEditable: Boolean
        get() = isEditingSubmitted || current == null || current?.status == RMP_STATUS_DRAFT

    fun submit() {
        val type = selectedType ?: return
        if (isEditingSubmitted) {
            saveSubmitted(type)
            return
        }
        runSaving {
            val data = valuesToJson(type.fields, values)
            var request =
                current?.let { repository.update(it.id, RMP_STATUS_DRAFT, data) }
                    ?: repository.create(type.value, RMP_STATUS_DRAFT, data)
            current = request
            pendingFiles.forEach { (field, files) ->
                files.forEach { file -> repository.uploadAttachment(request.id, field, file.name, file.bytes) }
            }
            if (pendingFiles.isNotEmpty()) {
                pendingFiles = emptyMap()
                request = repository.load(request.id)
                current = request
            }
            val sent = repository.update(request.id, RMP_STATUS_PENDING, data)
            newForm()
            feedback = RmpNewFeedback("Formulário ${number(sent)} enviado.", isError = false)
        }
    }

    /**
     * Salva um RMP enviado: sobe os PDFs escolhidos antes (a API confere os anexos obrigatórios ao salvar) e grava os
     * campos; o status não muda.
     */
    private fun saveSubmitted(type: RmpFormType) {
        val request = current ?: return
        runSaving {
            if (pendingFiles.isNotEmpty()) {
                pendingFiles.forEach { (field, files) ->
                    files.forEach { file -> repository.uploadSubmittedAttachment(request.id, field, file.name, file.bytes) }
                }
                pendingFiles = emptyMap()
                // Os PDFs já ficam no RMP mesmo se a gravação dos campos for recusada em seguida.
                current = repository.loadApproval(request.id).request
            }
            val saved = repository.updateSubmitted(request.id, valuesToJson(type.fields, values)).request
            current = saved
            values = jsonToValues(type.fields, saved.data)
            feedback = RmpNewFeedback("Alterações do RMP ${number(saved)} salvas.", isError = false)
        }
    }

    private fun runSaving(block: suspend () -> Unit) {
        if (saving) return
        saving = true
        feedback = null
        scope.launch {
            try {
                block()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                feedback = RmpNewFeedback(throwable.message ?: "Não foi possível salvar o formulário.", isError = true)
            } finally {
                saving = false
            }
        }
    }

    private fun number(request: RmpRequestDetail): String = request.number?.let { "#$it" } ?: ""
}

@Composable
fun rememberRmpNewController(): RmpNewController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember { RmpNewController(appGraph.rmpRequestRepository, scope) }
}
